export type ExperienceAtfCardAction = { type: 'link'; href: string } | { type: 'video'; videoId: string };

/** Matches ATF promo templates in Figma (node 7374:9689 + 7633:25654 + 7665:32868). */
export type ExperienceAtfPromoVariant =
  | 'agentic'
  | 'apr_2026_flash'
  | 'free'
  | 'marty_cagan_webinar'
  | 'mba'
  | 'webinar';

export type ExperienceAtfCardConfig = {
  id: string;
  action: ExperienceAtfCardAction;
  ctaLabel: string;
  description: string;
  eyebrow: string;
  promoVariant: ExperienceAtfPromoVariant;
  /** Primary heading (H2 / tests). */
  title: string;
  /** Optional second line (Agentic card). */
  titleLine2?: string;
};

/** Static art for ATF promos (`public/images/experiments/ab_experience_atf_2026/`). */
export const ATF_PROMO_ASSETS = {
  agenticAiBg: '/images/experiments/ab_experience_atf_2026/agentic-ai-bg.png',
  flashPromoBg: '/images/experiments/ab_experience_atf_2026/48hr-flash-promo-bg.png',
  martyCaganPortrait: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-portrait.png',
  martyCaganWaveBg: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-wave-bg.png',
  mbaBg: '/images/experiments/ab_experience_atf_2026/mba-bg.png',
  resourcesVideoThumbnail: '/images/accreditation/resources-video-thumbnail.png',
  schoolsGrid: '/images/experiments/ab_experience_atf_2026/schools-grid.png',
} as const;

/**
 * Intrinsic pixel size of each file (must match what is on disk).
 * MBA art is exported @2x for a 300×375 CSS card; schools grid is still @1x until re-exported.
 * Flash promo bg is also @2x (600×750 for a 300×375 shell).
 */
export const ATF_PROMO_ASSET_INTRINSIC_PX = {
  flashPromoBg: { height: 750, width: 600 },
  martyCaganPortrait: { height: 280, width: 232 },
  martyCaganWaveBg: { height: 750, width: 600 },
  mbaBg: { height: 750, width: 600 },
  schoolsGrid: { height: 158, width: 205 },
} as const;

/** Desktop promo shell (Figma 7374:9689). */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX = 300;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX = 375;

/**
 * Mobile promo shell (Figma 7382:11004 — node 7382:11940 frame).
 * Outer slide width keeps a 5px inset each side, same as desktop.
 */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE = 260;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE = 325;

/** Swiper slide / static SSR row cell width (px); matches card outer width. */
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX = 310;
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE = 270;

/**
 * Min height for the carousel row (avoids CLS). Shell height + vertical inset (desktop 375+13, mobile 325+13).
 */
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX = 388;
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE = 338;

/** CONENG-2167 promo order (free → agentic → mba → webinar). */
export const EXPERIENCE_ATF_CARDS: ExperienceAtfCardConfig[] = [
  {
    id: 'free_courses',
    promoVariant: 'free',
    eyebrow: 'Free programs',
    title: 'Start building AI skills for free',
    description: 'Explore top-rated courses and start today.',
    ctaLabel: 'Start Free Today',
    action: { type: 'link', href: '/catalog?price=free' },
  },
  {
    id: 'agentic_ai_ns',
    promoVariant: 'agentic',
    eyebrow: 'Featured program',
    title: 'Agentic AI is here',
    description: 'Go from chatbots to coordinated AI agent teams.',
    ctaLabel: 'Enroll Now',
    action: { type: 'link', href: '/course/agentic-ai--nd900' },
  },
  {
    id: 'mba_ai',
    promoVariant: 'mba',
    eyebrow: 'New at Udacity',
    title: 'Level up with an MBA',
    description: 'Earn a real MBA credential designed for AI product leaders.',
    ctaLabel: 'Explore MBA in AI',
    action: { type: 'link', href: '/mba-ai-product-management' },
  },
  {
    id: 'webinar_qa',
    promoVariant: 'webinar',
    eyebrow: 'Degree program',
    title: 'MBA focused in AI product management',
    description: 'Learn how this degree path supports AI product leadership.',
    ctaLabel: 'Watch Now',
    action: { type: 'video', videoId: 'FtNTrrTAqNw' },
  },
];

export const EXPERIMENT_KEY = 'ab_experience_atf_2026';

/** Sanity `promotionId` that gates the flash-sale promo tile on /experience ATF. */
export const EXPERIENCE_ATF_PROMOTION_ID = 'april-48hrs';

/** First-position promo tile shown when the Sanity `april-48hrs` promotion is active (Figma 7831:7417). */
export const EXPERIENCE_ATF_FLASH_PROMO_CARD: ExperienceAtfCardConfig = {
  id: 'apr_2026_flash',
  promoVariant: 'apr_2026_flash',
  eyebrow: 'Flash Sale',
  title: '48% off, for 48 hrs.',
  description: "Don't wait to invest in new AI and tech skills. Countdown is on.",
  ctaLabel: 'Save Now',
  action: {
    type: 'link',
    href: '/quick-checkout?checkoutType=individual&billingFrequency=4month&checkoutPaymentType=credit-card',
  },
};

/** Marty Cagan live-session promo tile (Figma 7665:32868, CONENG-2172). Replaces `webinar_qa` when `isMartyCaganAtfSlotActive` is true (Apr 10 2026 8pm PT–Apr 23 2026 9am PT, LA). */
export const EXPERIENCE_ATF_MARTY_CAGAN_WEBINAR_CARD: ExperienceAtfCardConfig = {
  id: 'marty_cagan_webinar_apr2026',
  promoVariant: 'marty_cagan_webinar',
  eyebrow: 'live session',
  title: 'The new standard for product managers',
  description: 'Learn what it takes to build, lead, and scale products in the age of AI.',
  ctaLabel: 'Register Now',
  action: { type: 'link', href: '/event/new-standard-for-product-managers' },
};
